/**
 * Per-TCG configuration registry. A lot is assigned a `game`, and that game's
 * config drives its lot-type options, its sorting-bin taxonomy, how cards
 * auto-categorize into bins, and how each bin maps onto the lot-analyzer's
 * revenue tiers.
 *
 * Adding a new TCG = add one `TcgGameConfig` entry to TCG_GAME_CONFIGS (or let
 * it fall through to GENERIC). No changes needed in the lot screens or the
 * analyzer — they read everything from `configForGame(lot.game)`.
 *
 * Pokémon reproduces the pre-framework behavior exactly (same categories,
 * icons, finish-aware categorization, and analyzer tiers), so existing lots
 * are unaffected.
 */
import {
  AlertTriangle,
  Award,
  Boxes,
  Disc,
  Gift,
  HelpCircle,
  Layers,
  type LucideIcon,
  Mountain,
  Package,
  Shield,
  Sparkles,
  Star,
  Sun,
  Zap,
} from "lucide-react";
import type { Card, CardGame } from "@/context/AppContext";

export type AnalyzerTier = "Bulk" | "Holo" | "Rare" | "Hits";

export interface SortingCategoryDef {
  /** Stable id — also the bin's `category`/`name` persisted on the lot. */
  id: string;
  label: string;
  icon: LucideIcon;
}

export interface LotTypeDef {
  id: string;
  /** Pre-fills the "estimated cards" field on lot create. */
  defaultCardCount?: number;
}

export interface TcgGameConfig {
  game: CardGame;
  label: string;
  lotTypes: LotTypeDef[];
  sortingCategories: SortingCategoryDef[];
  /** Map a card to a sorting-category id (auto-fill). Categories not produced
   *  here are manual-fill bins (e.g. Magic Mythics/Lands — see notes). */
  categorize: (card: Card) => string;
  /** Analyzer revenue tier for a category id. Unknown → "Bulk" (conservative). */
  categoryTier: (categoryId: string) => AnalyzerTier;
}

export const NEEDS_REVIEW = "Needs Review";

// ─── Pokémon (faithful to pre-framework behavior) ────────────────────────
const POKEMON: TcgGameConfig = {
  game: "Pokemon",
  label: "Pokémon",
  lotTypes: [
    { id: "Collection" },
    { id: "Booster Pack", defaultCardCount: 10 },
    { id: "Booster Box", defaultCardCount: 360 },
    { id: "Elite Trainer Box", defaultCardCount: 85 },
    { id: "Special Pack", defaultCardCount: 50 },
    { id: "Bulk" },
  ],
  sortingCategories: [
    { id: "Common/Uncommon Bulk", label: "Common/Uncommon Bulk", icon: Layers },
    {
      id: "Trainers/Items/Supporters",
      label: "Trainers/Items/Supporters",
      icon: Shield,
    },
    { id: "Energy", label: "Energy", icon: Zap },
    { id: "Rares", label: "Rares", icon: Star },
    { id: "Reverse Holos", label: "Reverse Holos", icon: Disc },
    { id: "Holos", label: "Holos", icon: Sun },
    { id: "Hits", label: "Hits", icon: Award },
    { id: "Promos", label: "Promos", icon: Gift },
    { id: "Damaged", label: "Damaged", icon: AlertTriangle },
    { id: "Sealed Product", label: "Sealed Product", icon: Package },
    { id: NEEDS_REVIEW, label: NEEDS_REVIEW, icon: HelpCircle },
  ],
  categorize: (card) => {
    if (card.condition === "Damaged") return "Damaged";
    if (card.finish === "Premium") return "Hits";
    if (card.finish === "Holo") return "Holos";
    if (card.finish === "Reverse Holo") return "Reverse Holos";
    switch (card.rarity) {
      case "Common":
      case "Uncommon":
        return "Common/Uncommon Bulk";
      case "Trainer":
        return "Trainers/Items/Supporters";
      case "Energy":
        return "Energy";
      case "Rare":
        return "Rares";
      case "Promo":
        return "Promos";
      case "Sealed":
        return "Sealed Product";
      default:
        return NEEDS_REVIEW;
    }
  },
  categoryTier: (id) => {
    switch (id) {
      case "Holos":
      case "Reverse Holos":
      case "Promos":
        return "Holo";
      case "Rares":
        return "Rare";
      case "Hits":
        return "Hits";
      default:
        return "Bulk";
    }
  },
};

// ─── Magic: The Gathering (seeded — rarity + type hybrid) ─────────────────
// Mythics auto-fill (rarity enum carries "Mythic") and Lands auto-fill from
// the card's parsed type line (tcgTypes, populated by the Scryfall-backed
// catalog). Foil takes precedence over Land so a foil land lands in Foils.
const MAGIC: TcgGameConfig = {
  game: "Magic",
  label: "Magic: The Gathering",
  lotTypes: [
    { id: "Collection" },
    { id: "Booster Pack", defaultCardCount: 15 },
    { id: "Booster Box", defaultCardCount: 540 },
    { id: "Bundle", defaultCardCount: 120 },
    { id: "Commander Deck", defaultCardCount: 100 },
    { id: "Bulk" },
  ],
  sortingCategories: [
    { id: "Commons", label: "Commons", icon: Layers },
    { id: "Uncommons", label: "Uncommons", icon: Boxes },
    { id: "Rares", label: "Rares", icon: Star },
    { id: "Mythics", label: "Mythics", icon: Award },
    { id: "Lands", label: "Lands", icon: Mountain },
    { id: "Foils", label: "Foils", icon: Sparkles },
    { id: "Damaged", label: "Damaged", icon: AlertTriangle },
    { id: NEEDS_REVIEW, label: NEEDS_REVIEW, icon: HelpCircle },
  ],
  categorize: (card) => {
    if (card.condition === "Damaged") return "Damaged";
    if (card.finish === "Holo" || card.finish === "Premium") return "Foils";
    if (card.tcgTypes?.some((t) => t.toLowerCase() === "land")) return "Lands";
    switch (card.rarity) {
      case "Common":
        return "Commons";
      case "Uncommon":
        return "Uncommons";
      case "Rare":
        return "Rares";
      case "Mythic":
        return "Mythics";
      default:
        return NEEDS_REVIEW;
    }
  },
  categoryTier: (id) => {
    switch (id) {
      case "Rares":
        return "Rare";
      case "Mythics":
      case "Foils":
        return "Hits";
      default:
        return "Bulk";
    }
  },
};

// ─── Yu-Gi-Oh! (seeded) ───────────────────────────────────────────────────
// Yu-Gi-Oh rarity is largely a foil/treatment story (Super/Ultra/Secret Rare),
// which the shared model carries on the `finish` axis (→ Premium) rather than
// the pull-rate `rarity` axis. So the "Holos/Foils" bin captures every premium
// tier; Commons and Rares split off the non-foil bulk.
const YUGIOH: TcgGameConfig = {
  game: "Yu-Gi-Oh",
  label: "Yu-Gi-Oh!",
  lotTypes: [
    { id: "Collection" },
    { id: "Booster Pack", defaultCardCount: 9 },
    { id: "Booster Box", defaultCardCount: 216 },
    { id: "Structure Deck", defaultCardCount: 43 },
    { id: "Tin", defaultCardCount: 45 },
    { id: "Bulk" },
  ],
  sortingCategories: [
    { id: "Commons", label: "Commons", icon: Layers },
    { id: "Rares", label: "Rares", icon: Star },
    { id: "Holos/Foils", label: "Holos/Foils", icon: Sparkles },
    { id: "Damaged", label: "Damaged", icon: AlertTriangle },
    { id: NEEDS_REVIEW, label: NEEDS_REVIEW, icon: HelpCircle },
  ],
  categorize: (card) => {
    if (card.condition === "Damaged") return "Damaged";
    if (card.finish === "Holo" || card.finish === "Premium")
      return "Holos/Foils";
    switch (card.rarity) {
      case "Common":
      case "Uncommon":
        return "Commons";
      case "Rare":
        return "Rares";
      default:
        return NEEDS_REVIEW;
    }
  },
  categoryTier: (id) => {
    switch (id) {
      case "Rares":
        return "Rare";
      case "Holos/Foils":
        return "Hits";
      default:
        return "Bulk";
    }
  },
};

// ─── One Piece (seeded) ───────────────────────────────────────────────────
// Common / Uncommon / Rare split the non-foil bulk; Super/Secret Rares and
// foils ride the `finish` axis into the "Super Rares" bin. Leaders aren't
// distinguishable from rarity/finish alone — manual-fill, like a catch-all.
const ONE_PIECE: TcgGameConfig = {
  game: "One Piece",
  label: "One Piece",
  lotTypes: [
    { id: "Collection" },
    { id: "Booster Pack", defaultCardCount: 12 },
    { id: "Booster Box", defaultCardCount: 144 },
    { id: "Starter Deck", defaultCardCount: 51 },
    { id: "Bulk" },
  ],
  sortingCategories: [
    { id: "Commons", label: "Commons", icon: Layers },
    { id: "Uncommons", label: "Uncommons", icon: Boxes },
    { id: "Rares", label: "Rares", icon: Star },
    { id: "Super Rares", label: "Super Rares", icon: Sparkles },
    { id: "Leaders", label: "Leaders", icon: Award },
    { id: "Damaged", label: "Damaged", icon: AlertTriangle },
    { id: NEEDS_REVIEW, label: NEEDS_REVIEW, icon: HelpCircle },
  ],
  categorize: (card) => {
    if (card.condition === "Damaged") return "Damaged";
    if (card.finish === "Holo" || card.finish === "Premium")
      return "Super Rares";
    switch (card.rarity) {
      case "Common":
        return "Commons";
      case "Uncommon":
        return "Uncommons";
      case "Rare":
        return "Rares";
      default:
        return NEEDS_REVIEW;
    }
  },
  categoryTier: (id) => {
    switch (id) {
      case "Rares":
        return "Rare";
      case "Super Rares":
      case "Leaders":
        return "Hits";
      default:
        return "Bulk";
    }
  },
};

// ─── Generic default (Other, any not-yet-seeded game) ─────────────────────
const GENERIC: TcgGameConfig = {
  game: "Other",
  label: "Other / Generic",
  lotTypes: [
    { id: "Collection" },
    { id: "Booster Pack" },
    { id: "Booster Box" },
    { id: "Bulk" },
  ],
  sortingCategories: [
    { id: "Commons", label: "Commons", icon: Layers },
    { id: "Uncommons", label: "Uncommons", icon: Boxes },
    { id: "Rares", label: "Rares", icon: Star },
    { id: "Foils", label: "Foils", icon: Sparkles },
    { id: "Damaged", label: "Damaged", icon: AlertTriangle },
    { id: NEEDS_REVIEW, label: NEEDS_REVIEW, icon: HelpCircle },
  ],
  categorize: (card) => {
    if (card.condition === "Damaged") return "Damaged";
    if (card.finish === "Holo" || card.finish === "Premium") return "Foils";
    switch (card.rarity) {
      case "Common":
        return "Commons";
      case "Uncommon":
        return "Uncommons";
      case "Rare":
        return "Rares";
      default:
        return NEEDS_REVIEW;
    }
  },
  categoryTier: (id) => {
    switch (id) {
      case "Rares":
        return "Rare";
      case "Foils":
        return "Hits";
      default:
        return "Bulk";
    }
  },
};

export const TCG_GAME_CONFIGS: Record<CardGame, TcgGameConfig> = {
  Pokemon: POKEMON,
  Magic: MAGIC,
  "Yu-Gi-Oh": YUGIOH,
  "One Piece": ONE_PIECE,
  Other: GENERIC,
};

/** Resolve a game's config; undefined/unknown falls back to Pokémon so legacy
 *  lots and the DB column default ('Pokemon') agree. */
export function configForGame(game: CardGame | undefined): TcgGameConfig {
  if (!game) return POKEMON;
  return TCG_GAME_CONFIGS[game] ?? POKEMON;
}

/** All games offered in the lot picker, in display order. */
export const GAME_PICKER_OPTIONS: CardGame[] = [
  "Pokemon",
  "Magic",
  "Yu-Gi-Oh",
  "One Piece",
  "Other",
];
